import * as React from 'react';
import { CurrencyDisplayStyle } from '../../components/DataDisplay/CurrencyDisplayStyle';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { MatterTerminology } from '../../MatterTerminology';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { FormattedCurrency } from '../../components/DataDisplay/FormattedCurrency';
import { PageNotFound } from '../../components/PageNotFound';
import moment from 'moment';
import FinancialByDayChart from './FinancialByDayChart';
import EstimateVsBilledFeesChart from './EstimateVsBilledFeesChart';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';
import { WithTranslation, withTranslation } from 'react-i18next';
import { TranslationTextList } from '../../locales/common';
import { DateTimeDisplayStyle, FormattedDateTime } from '../../components/DataDisplay/FormattedDateTime';
import { CurrenciesData } from '../../firmSettingConfiguration/common/Currencies';
import { formatNumber } from '../../helpers/ChartHelper';
import { Card, Theme, createStyles, withStyles, WithStyles } from '@material-ui/core';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import EventSystem from '../../EventSystem';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import HourglassEmptyOutlinedIcon from '@material-ui/icons/HourglassEmptyOutlined';
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import StarOutlineIcon from '@material-ui/icons/StarOutline';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

// Explicit request: same tile treatment ("Do a similar treatment to the Financials tab like
// we did in the enquiry matter cards") as EnquiryFormDrawer.tsx's own Matter tab financials -
// an icon chip + label + value + short description per figure, instead of the old plain
// glamorous Card grid. Colours reuse this app's own --rv-accent-* tokens (App.css), matching
// EnquiryFormDrawer.tsx/HistoryActivity.tsx's own icon language, and (for the hours/standard
// value/profitability tiles new to this richer page) RedView Mobile V2's own
// MatterFinancialsTab.tsx icon choices (briefcase/clock/star/trending-up), for consistency
// across both apps rather than inventing a third set of choices.
const styles = (theme: Theme) =>
    createStyles({
        wrap: {
            display: 'flex',
            gap: theme.spacing(3),
            padding: theme.spacing(2.5, 3, 0, 3),
            alignItems: 'flex-start',
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
            },
        },
        tileSection: {
            flex: '1 1 60%',
            minWidth: 0,
        },
        tileGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: theme.spacing(2),
        },
        chartSection: {
            flex: '1 1 40%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
        },
        chartBox: {
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 8,
            padding: theme.spacing(2),
            minHeight: 440,
            display: 'flex',
            flexDirection: 'column',
        },
        tile: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 8,
            padding: theme.spacing(2),
        },
        iconChip: {
            width: 28,
            height: 28,
            minWidth: 28,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginBottom: theme.spacing(1),
        },
        tileLabel: {
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            marginBottom: 4,
        },
        tileValue: {
            fontSize: 20,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
        },
        tileValueString: {
            fontSize: 15,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
        },
        tileSubheading: {
            fontSize: 11,
            color: theme.palette.text.hint,
            marginTop: 4,
        },
    });

// tslint:disable-next-line:no-any
const FinancialTile = withStyles(styles)((props: WithStyles<typeof styles> & {
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    label: string;
    value: React.ReactNode;
    valueColor?: string;
    valueIsString?: boolean;
    subheading?: string;
}) => (
    <Card className={props.classes.tile}>
        <span className={props.classes.iconChip} style={{ backgroundColor: props.iconBg, color: props.iconColor }}>
            {props.icon}
        </span>
        <div className={props.classes.tileLabel}>{props.label}</div>
        <div
            className={props.valueIsString ? props.classes.tileValueString : props.classes.tileValue}
            style={{ color: props.valueColor }}
        >
            {props.value}
        </div>
        {!!props.subheading && <div className={props.classes.tileSubheading}>{props.subheading}</div>}
    </Card>
));

// tslint:disable-next-line:no-any
// const DebtorDueDiv = glamorous.div<{theme?: any}> ((props) => ({
//     [`& .${`rvlabel`}`] : {
//         color: props.theme.BrandColors.TextPrimary,
//         fontSize: props.theme.FontSize.PX.size4 + ' !important',
//         lineHeight: props.theme.LineHeight.Regular2,
//     }
// }));

// const LastGrid = glamorous(Grid) ({
//     [`& .${`LabelData`}`] : {
//         paddingLeft: '25px !important'
//     },
// });

interface ExternalProps {
    id: string;
    sourceSystem: string;
    isInternalUser: boolean;
}

export interface MatterFinancialsData {
    estimatedCosts: number;
    billedTimeAndFees: number;
    billedDisbursements: number;
    unbilledTimeAndFees: number;
    unbilledDisbursements: number;
    balanceDue: number;
    trustDeposits: number;
    trustWithdrawals: number;
    trustGeneralBalance: number;
    trustControlledMoneyBalance: number;
    trustInvestmentBalance: number;
    billingMethod: string;
    costAgreementReceived: string;
    agedDebtorCurrent: number;
    agedDebtor30Days: number;
    agedDebtor60Days: number;
    agedDebtor90Days: number;
    lastTrustTransactionDate: Date;
    lastTrustTransactionType: string;
    foreignCurrency: string;
    foreignCurrencyBilledTimeAndFees: number;
    foreignCurrencyBalanceDue: number;
    foreignCurrencyUnbilledTimeAndFees: number;
    foreignCurrencyBilledDisbursements: number;
    foreignCurrencyUnbilledDisbursements: number;
    billableHours: number;
    nonBillableHours: number;
    totalHours: number;
    standardValue: number;
    profitability: number;
}

export interface MatterData {
    loading?: boolean;
    networkStatus?: number;
    matter: Matter;
    refetch: () => void;
}

export interface Matter {
    title: string;
    client: string;
    nameID: Number;
    financials?: MatterFinancialsData;
    id: Number;
}

interface MatterFinancialsInfoProps extends ExternalProps, WithTranslation, WithStyles<typeof styles> {
    data: MatterData;
    currencySymbol: string;
    homeCountry: string;
}

// tslint:disable-next-line:no-any
class RvMatterFinancialsInfo extends React.Component<MatterFinancialsInfoProps, {}> {
    constructor(props: MatterFinancialsInfoProps) {
        super(props);

        this.getCurrencySymbol = this.getCurrencySymbol.bind(this);
        this.formatCurrencyAndForeignCurrency = this.formatCurrencyAndForeignCurrency.bind(this);
    }

    componentDidMount() {
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    componentWillUnmount() {
        EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    private onRefresh = () => {
        this.props.data.refetch();
    }

    render() {
        let displayZero = 0.000000000001;

        // tslint:disable-next-line: no-console
        console.log('symbol', this.props.currencySymbol);

        if (this.props.data.loading) {
            const classes = this.props.classes;
            return (
                <div className={classes.wrap}>
                    <div className={classes.tileSection}>
                        <div className={classes.tileGrid}>
                            {Array.from({ length: 9 }).map((_, index) => (
                                <div key={index} className={classes.tile}>
                                    <SkeletonLoader variant="card" rows={1} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={classes.chartSection}>
                        <div className={classes.chartBox}>
                            <SkeletonLoader variant="panel" columns={1} rows={4} wrapInPaper={false} />
                        </div>
                        <div className={classes.chartBox}>
                            <SkeletonLoader variant="panel" columns={1} rows={4} wrapInPaper={false} />
                        </div>
                    </div>
                </div>
            );
        }

        var financials = this.props.data.matter.financials;
        if (!financials) {
            return <PageNotFound />;
        }

        let foreignCurrencySybmol = this.props.currencySymbol;
        let showForeignCurrencyAmount = false;
        let showBilledTimeAndFees = true;
        let showBalanceDue = true;
        let showUnbilledTimeAndFees = true;
        let showBilledDisbursements = true;
        let showUnbilledDisbursements = true;

        let billedTimeAndFeesCurrencySymbol = this.getCurrencySymbol(this.props.currencySymbol);
        let balanceDueCurrencySymbol = this.getCurrencySymbol(this.props.currencySymbol);
        let unbilledTimeAndFeesCurrencySymbol = this.getCurrencySymbol(this.props.currencySymbol);
        let billedDisbursementsCurrencySymbol = this.getCurrencySymbol(this.props.currencySymbol);

        let unbilledDisbursementsCurrencySymbol = this.getCurrencySymbol(this.props.currencySymbol);

        if (this.props.sourceSystem.toLocaleLowerCase() === 'inprotech') {
            // foreignCurrencySybmol = financials.foreignCurrency ? financials.foreignCurrency : this.props.currencySymbol;

            if (financials && financials.foreignCurrency) {
                // var sybmol = CurrenciesData.find(currencyItem => currencyItem.currency === financials?.foreignCurrency);
                var sybmol = CurrenciesData.find((currencyItem) => currencyItem.currency.toLowerCase().includes(financials!.foreignCurrency.toLowerCase()));

                if (sybmol) {
                    foreignCurrencySybmol = sybmol.currency;
                }

                showForeignCurrencyAmount = true;
            }

            if (this.props.isInternalUser === false) {
                // For External User - Prioritise the Foreign Currency display only

                if (showForeignCurrencyAmount) {
                    showBilledTimeAndFees = false;
                    showBalanceDue = false;
                    showUnbilledTimeAndFees = false;
                    showBilledDisbursements = false;
                    showUnbilledDisbursements = false;
                }
            }
        }

        var costAgreementReceivedColor: string | undefined = financials.costAgreementReceived ? undefined : '#C0C0C0';

        var billedTimeAndFees = financials.billedTimeAndFees ? financials.billedTimeAndFees : 0;
        var unbilledTimeAndFees = financials.unbilledTimeAndFees ? financials.unbilledTimeAndFees : 0;
        var estimatedFees = financials.estimatedCosts ? financials.estimatedCosts : 0;

        var estimatedFeesColour: string | undefined = billedTimeAndFees + unbilledTimeAndFees > estimatedFees ? 'red' : undefined;
        // let vm = this;

        const { t } = this.props as {
            t: (key: keyof TranslationTextList) => string;
        };

        const classes = this.props.classes;
        const isProfitable = (financials.profitability ?? 0) >= 0;

        // Tiles are conditionally shown/hidden, so delays are assigned as each tile is
        // actually rendered (in source order) rather than by a fixed index.
        let nextTileDelay = 0;
        const animatedTile = (node: React.ReactNode) => {
            const delay = nextTileDelay;
            nextTileDelay += 30;
            return (
                <AnimatedItem key={delay} noFill delay={delay}>
                    {node}
                </AnimatedItem>
            );
        };

        return (
            <div className={classes.wrap}>
                <div className={classes.tileSection}>
                    <div className={classes.tileGrid}>
                        {animatedTile(
                            <FinancialTile
                                icon={<AssessmentOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-teal-bg)"
                                iconColor="var(--rv-accent-teal)"
                                label={MatterTerminology.EstimatedCosts}
                                value={(
                                    <FormattedCurrency
                                        // tslint:disable-next-line:max-line-length
                                        value={financials.estimatedCosts ? financials.estimatedCosts : displayZero}
                                        displayStyle={CurrencyDisplayStyle.Integer}
                                        format={this.props.currencySymbol}
                                    />
                                )}
                                valueColor={estimatedFeesColour}
                                subheading={estimatedFeesColour ? 'Over the quoted budget' : 'Quoted budget'}
                            />
                        )}
                        {animatedTile(
                            <FinancialTile
                                icon={<CheckCircleOutlineIcon fontSize="small" />}
                                iconBg="var(--rv-accent-grey-bg)"
                                iconColor="var(--rv-accent-grey)"
                                label={MatterTerminology.Billed + ' ' + MatterTerminology.BilledTimeAndFees}
                                value={this.formatCurrencyAndForeignCurrency({
                                    showLocalCurrency: showBilledTimeAndFees,
                                    localCurrency: billedTimeAndFeesCurrencySymbol,
                                    localValue: financials.billedTimeAndFees,
                                    showForeignCurrency: showForeignCurrencyAmount,
                                    foreignCurrency: foreignCurrencySybmol,
                                    foreignValue: financials.foreignCurrencyBilledTimeAndFees
                                })}
                                subheading="Already invoiced"
                            />
                        )}
                        {animatedTile(
                            <FinancialTile
                                icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-red-bg)"
                                iconColor="var(--rv-accent-red)"
                                label={MatterTerminology.TotalOwing}
                                value={this.formatCurrencyAndForeignCurrency({
                                    showLocalCurrency: showBalanceDue,
                                    localCurrency: balanceDueCurrencySymbol,
                                    localValue: financials.balanceDue,
                                    showForeignCurrency: showForeignCurrencyAmount,
                                    foreignCurrency: foreignCurrencySybmol,
                                    foreignValue: financials.foreignCurrencyBalanceDue
                                })}
                                subheading="Outstanding from client"
                            />
                        )}
                        {UserFeature.HasAccess(UserFeatureEnum.hasMatterWIPInformation) && animatedTile(
                            <FinancialTile
                                icon={<HourglassEmptyOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-orange-bg)"
                                iconColor="var(--rv-accent-orange)"
                                label={
                                    // tslint:disable-next-line:max-line-length
                                    MatterTerminology.Unbilled + ' ' + MatterTerminology.UnbilledTimeAndFees
                                }
                                value={this.formatCurrencyAndForeignCurrency({
                                    showLocalCurrency: showUnbilledTimeAndFees,
                                    localCurrency: unbilledTimeAndFeesCurrencySymbol,
                                    localValue: financials.unbilledTimeAndFees,
                                    showForeignCurrency: showForeignCurrencyAmount,
                                    foreignCurrency: foreignCurrencySybmol,
                                    foreignValue: financials.foreignCurrencyUnbilledTimeAndFees
                                })}
                                subheading="To be billed"
                            />
                        )}
                        {!showForeignCurrencyAmount ? animatedTile(
                            <FinancialTile
                                icon={<ListAltOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-grey-bg)"
                                iconColor="var(--rv-accent-grey)"
                                label={MatterTerminology.BillingMethod}
                                value={financials.billingMethod}
                                valueIsString={true}
                            />
                        ) : null}
                        {animatedTile(
                            <FinancialTile
                                icon={<DescriptionOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-indigo-bg)"
                                iconColor="var(--rv-accent-indigo)"
                                label={MatterTerminology.CostAgreementReceived}
                                value={
                                    // tslint:disable-next-line:max-line-length
                                    financials.costAgreementReceived ? moment(financials.costAgreementReceived).format('DD MMM YYYY') : '—'
                                }
                                valueColor={costAgreementReceivedColor}
                                valueIsString={true}
                            />
                        )}
                        {animatedTile(
                            <FinancialTile
                                icon={<ReceiptOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-grey-bg)"
                                iconColor="var(--rv-accent-grey)"
                                label={MatterTerminology.Billed + ' ' + MatterTerminology.Disbursements}
                                value={this.formatCurrencyAndForeignCurrency({
                                    showLocalCurrency: showBilledDisbursements,
                                    localCurrency: billedDisbursementsCurrencySymbol,
                                    localValue: financials.billedDisbursements,
                                    showForeignCurrency: showForeignCurrencyAmount,
                                    foreignCurrency: foreignCurrencySybmol,
                                    foreignValue: financials.foreignCurrencyBilledDisbursements
                                })}
                                subheading="Already invoiced"
                            />
                        )}
                        {animatedTile(
                            <FinancialTile
                                icon={<VerifiedUserOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-teal-bg)"
                                iconColor="var(--rv-accent-teal)"
                                label={t('Trust Balance')}
                                value={(
                                    <FormattedCurrency
                                        // tslint:disable-next-line:max-line-length
                                        value={financials.trustGeneralBalance ? financials.trustGeneralBalance : displayZero}
                                        displayStyle={CurrencyDisplayStyle.Integer}
                                        format={this.props.currencySymbol}
                                    />
                                )}
                                subheading="Held on trust"
                            />
                        )}
                        {UserFeature.HasAccess(UserFeatureEnum.hasMatterWIPInformation) && animatedTile(
                            <FinancialTile
                                icon={<ReceiptOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-orange-bg)"
                                iconColor="var(--rv-accent-orange)"
                                label={MatterTerminology.Unbilled + ' ' + MatterTerminology.Disbursements}
                                value={this.formatCurrencyAndForeignCurrency({
                                    showLocalCurrency: showUnbilledDisbursements,
                                    localCurrency: unbilledDisbursementsCurrencySymbol,
                                    localValue: financials.unbilledDisbursements,
                                    showForeignCurrency: showForeignCurrencyAmount,
                                    foreignCurrency: foreignCurrencySybmol,
                                    foreignValue: financials.foreignCurrencyUnbilledDisbursements
                                })}
                                subheading="To be billed"
                            />
                        )}
                        {!showForeignCurrencyAmount ? animatedTile(
                            <FinancialTile
                                icon={<LockOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-teal-bg)"
                                iconColor="var(--rv-accent-teal)"
                                label={MatterTerminology.TrustControlledMoneyBalance}
                                value={(
                                    <FormattedCurrency
                                        value={financials.trustControlledMoneyBalance}
                                        displayStyle={CurrencyDisplayStyle.Integer}
                                        format={this.props.currencySymbol}
                                    />
                                )}
                                subheading="Controlled money account"
                            />
                        ) : null}
                        {financials.lastTrustTransactionDate ? animatedTile(
                            <FinancialTile
                                icon={<EventOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-teal-bg)"
                                iconColor="var(--rv-accent-teal)"
                                label={t(`Last ${this.props.homeCountry.toLowerCase() === 'au' ? 'Trust' : 'Client Account'} Transaction Date`)}
                                value={<FormattedDateTime value={financials.lastTrustTransactionDate} displayStyle={DateTimeDisplayStyle.ShortDate} />}
                                valueIsString={true}
                            />
                        ) : null}
                        {financials.lastTrustTransactionType ? animatedTile(
                            <FinancialTile
                                icon={<EventOutlinedIcon fontSize="small" />}
                                iconBg="var(--rv-accent-teal-bg)"
                                iconColor="var(--rv-accent-teal)"
                                label={`Last ${this.props.homeCountry.toLowerCase() === 'au' ? 'Trust' : 'Client Account'} Transaction Type`}
                                value={financials.lastTrustTransactionType}
                                valueIsString={true}
                            />
                        ) : null}
                        {UserFeature.HasAccess(UserFeatureEnum.hasMatterFinancialHours) ? animatedTile(
                            <FinancialTile
                                icon={<WorkOutlineIcon fontSize="small" />}
                                iconBg="var(--rv-accent-teal-bg)"
                                iconColor="var(--rv-accent-teal)"
                                label="Billable Hours"
                                value={formatNumber(financials.billableHours || 0, 2)}
                            />
                        ) : null}
                        {UserFeature.HasAccess(UserFeatureEnum.hasMatterFinancialHours) ? animatedTile(
                            <FinancialTile
                                icon={<AccessTimeIcon fontSize="small" />}
                                iconBg="var(--rv-accent-grey-bg)"
                                iconColor="var(--rv-accent-grey)"
                                label="Non-Billable Hours"
                                value={formatNumber(financials.nonBillableHours || 0, 2)}
                            />
                        ) : null}
                        {UserFeature.HasAccess(UserFeatureEnum.hasMatterFinancialProfitability) ? animatedTile(
                            <FinancialTile
                                icon={<StarOutlineIcon fontSize="small" />}
                                iconBg="var(--rv-accent-orange-bg)"
                                iconColor="var(--rv-accent-orange)"
                                label="Standard Value"
                                value={(
                                    <FormattedCurrency
                                        // tslint:disable-next-line:max-line-length
                                        value={financials.standardValue ? financials.standardValue : displayZero}
                                        displayStyle={CurrencyDisplayStyle.Integer}
                                        format={this.props.currencySymbol}
                                    />
                                )}
                            />
                        ) : null}
                        {UserFeature.HasAccess(UserFeatureEnum.hasMatterFinancialProfitability) ? animatedTile(
                            <FinancialTile
                                icon={<TrendingUpOutlinedIcon fontSize="small" />}
                                iconBg={isProfitable ? 'var(--rv-accent-green-bg)' : 'var(--rv-accent-red-bg)'}
                                iconColor={isProfitable ? 'var(--rv-accent-green)' : 'var(--rv-accent-red)'}
                                label="Profitability"
                                value={financials.profitability ? `${formatNumber(financials.profitability)}%` : '0%'}
                            />
                        ) : null}
                    </div>
                </div>
                <div className={classes.chartSection}>
                    {animatedTile(
                        <div className={classes.chartBox}>
                            <EstimateVsBilledFeesChart
                                labels={['Estimated Fees', 'Billed Time & Fees']}
                                data={[
                                    // tslint:disable-next-line:max-line-length
                                    financials.estimatedCosts ? financials.estimatedCosts : displayZero,
                                    // tslint:disable-next-line:max-line-length
                                    financials.billedTimeAndFees ? financials.billedTimeAndFees : displayZero,
                                ]}
                            />
                        </div>
                    )}
                    {animatedTile(
                        <div className={classes.chartBox}>
                            <FinancialByDayChart
                                labels={['Current', '30 days', '60 days', '90+ days']}
                                data={[
                                    financials.agedDebtorCurrent ? financials.agedDebtorCurrent : 0,
                                    financials.agedDebtor30Days ? financials.agedDebtor30Days : 0,
                                    financials.agedDebtor60Days ? financials.agedDebtor60Days : 0,
                                    financials.agedDebtor90Days ? financials.agedDebtor90Days : 0,
                                ]}
                            />
                        </div>
                    )}
                </div>
                {/* Legacy alternate layout, already unused before this redesign - kept as a
                    reference for the field set (unbilledAnticipatedPayments/billedCostsAndPayments/
                    trustInvestmentBalance) rather than deleted outright.
                <Grid>
                    <Grid.Column
                        computer={8}
                        tablet={16}
                        mobile={16}
                        largeScreen={8}
                        widescreen={8}
                    >
                        <InfoPanel>
                            <RvLabel className="title" label={MatterTerminology.Unbilled}/>
                            <CurrencyDisplay
                                label={MatterTerminology.UnbilledTimeAndFees}
                                value={financials.unbilledTimeAndFees ?
                                    financials.unbilledTimeAndFees : displayZero}
                                displayStyle={CurrencyDisplayStyle.Decimal}
                                tabbed={true}
                            />
                            <CurrencyDisplay
                                label={MatterTerminology.Disbursements}
                                value={financials.unbilledAnticipatedPayments || financials.unbilledOfficePayments ?
                                    financials.unbilledAnticipatedPayments + financials.unbilledOfficePayments
                                    : displayZero}
                                displayStyle={CurrencyDisplayStyle.Decimal}
                                tabbed={true}
                            />
                        </InfoPanel>
                        <InfoPanel>
                            <RvLabel className="title" label={MatterTerminology.TrustFund} />
                            <CurrencyDisplay
                                label={MatterTerminology.TrustGeneralBalance}
                                value={financials.trustGeneralBalance ?
                                    financials.trustGeneralBalance : displayZero}
                                displayStyle={CurrencyDisplayStyle.Decimal}
                                tabbed={true}
                            />
                            {financials.trustControlledMoneyBalance !== 0 ?
                                <CurrencyDisplay
                                    label={MatterTerminology.TrustControlledMoneyBalance}
                                    value={financials.trustControlledMoneyBalance}
                                    displayStyle={CurrencyDisplayStyle.Decimal}
                                    tabbed={true}
                                /> : null}
                            {financials.trustInvestmentBalance !== 0 ?
                                <CurrencyDisplay
                                    label={MatterTerminology.TrustInvestmentsBalance}
                                    value={financials.trustInvestmentBalance}
                                    displayStyle={CurrencyDisplayStyle.Decimal}
                                    tabbed={true}
                                /> : null}
                        </InfoPanel>
                    </Grid.Column>

                    <Grid.Column
                        computer={8}
                        tablet={16}
                        mobile={16}
                        largeScreen={8}
                        widescreen={8}
                    >
                        <InfoPanel>
                            <RvLabel className="title" label={MatterTerminology.Billed}/>
                            <CurrencyDisplay
                                label={MatterTerminology.BilledTimeAndFees}
                                value={financials.billedTimeAndFees ? financials.billedTimeAndFees : displayZero}
                                displayStyle={CurrencyDisplayStyle.Decimal}
                                tabbed={true}
                            />
                        </InfoPanel>
                        <InfoPanel>
                            <DebtorDueDiv>
                                <CurrencyDisplay
                                    label={MatterTerminology.BalanceDue}
                                    value={financials.balanceDue ? financials.balanceDue : displayZero}
                                    displayStyle={CurrencyDisplayStyle.Decimal}
                                />
                            </DebtorDueDiv>
                            <CurrencyDisplay
                                label={MatterTerminology.BilledCostAndPayments}
                                value={financials.billedCostsAndPayments ?
                                    financials.billedCostsAndPayments : displayZero}
                                displayStyle={CurrencyDisplayStyle.Decimal}
                                tabbed={true}
                            />
                        </InfoPanel>
                    </Grid.Column>
                </Grid>
                <Divider/>
                <LastGrid>
                    <Grid.Column
                        computer={8}
                        tablet={16}
                        mobile={16}
                        largeScreen={8}
                        widescreen={8}
                    >
                        <InfoPanel>
                            <CurrencyDisplay
                                label={MatterTerminology.EstimatedCosts}
                                value={financials.estimatedCosts ? financials.estimatedCosts : displayZero}
                                displayStyle={CurrencyDisplayStyle.Decimal}
                                tabbed={true}
                            />
                            {financials.billingMethod ?
                                <RvStringDisplay
                                    label={MatterTerminology.BillingMethod}
                                    value={financials.billingMethod}
                                /> : null}
                            {financials.costAgreementReceived ?
                                <RvStringDisplay
                                    label={MatterTerminology.CostAgreementReceived}
                                    value={financials.costAgreementReceived}
                                    color={costAgreementReceivedColor}
                                /> : null}

                        </InfoPanel>
                    </Grid.Column>
                </LastGrid> */}
            </div>
        );
    }

    private getCurrencySymbol(currency: string) {
        return CurrenciesData.find((currencyItem) => currencyItem.currency.toLowerCase().includes(currency.toLowerCase()))?.symbol;
    }

    private formatCurrencyAndForeignCurrency(input: {
        showLocalCurrency: boolean;
        localCurrency: string | undefined;
        localValue: number;
        showForeignCurrency: boolean;
        foreignCurrency: string;
        foreignValue: number;
    }) {
        const { foreignCurrency, foreignValue, localCurrency, localValue, showForeignCurrency, showLocalCurrency } = input;

        if (showLocalCurrency && localValue !== 0 && showForeignCurrency && foreignValue !== 0) {
            return `${localCurrency}${formatNumber(localValue)}/${foreignCurrency}${formatNumber(foreignValue)}`;
        }

        if ((showLocalCurrency && localValue !== 0 && showForeignCurrency && foreignValue === 0) || (showLocalCurrency && localValue !== 0 && !showForeignCurrency)) {
            return `${localCurrency}${formatNumber(localValue)}`;
        }

        if ((!showLocalCurrency && localValue === 0 && showForeignCurrency && foreignValue !== 0) || (!showLocalCurrency && showForeignCurrency && foreignValue !== 0)) {
            return `${foreignCurrency}${formatNumber(foreignValue)}`;
        }

        if (!showLocalCurrency && localValue === 0 && !showForeignCurrency && foreignValue === 0) {
            return `${localCurrency}${formatNumber(localValue)}`;
        }

        if ((!showLocalCurrency && localValue !== 0 && showForeignCurrency && foreignValue === 0)) {
            return `${localCurrency}${formatNumber(localValue)}`;
        }

        if ((!showLocalCurrency && showForeignCurrency && foreignValue === 0) || (showLocalCurrency && showForeignCurrency && localValue === 0 && foreignValue === 0)) {
            return `${localCurrency}0`;
        }

        return '';
    }
}

export const FinancialsInfoData = gql`query FinancialsInfoData($id: Int!){
    matter(id: $id) {
        title,
        client,
        nameID,
        id,
        financials {
            estimatedCosts
            billedTimeAndFees
            billedDisbursements
            unbilledTimeAndFees
            unbilledDisbursements
            balanceDue
            trustDeposits
            trustWithdrawals
            trustGeneralBalance
            trustControlledMoneyBalance
            trustInvestmentBalance
            billingMethod
            costAgreementReceived
            agedDebtorCurrent
            agedDebtor30Days
            agedDebtor60Days
            agedDebtor90Days
            lastTrustTransactionDate
          	lastTrustTransactionType
            foreignCurrency
            foreignCurrencyBilledTimeAndFees
            foreignCurrencyBalanceDue
            foreignCurrencyUnbilledTimeAndFees
            foreignCurrencyBilledDisbursements
            foreignCurrencyUnbilledDisbursements
            billableHours
            nonBillableHours
            totalHours
            standardValue
            profitability
        }
    }
}`;

// tslint:disable-next-line:no-any
export const FinancialsInfo = withStyles(styles)(withTranslation()(graphql<any, ExternalProps, any>
    (FinancialsInfoData, {
        // Was inheriting the app's global default (cache-and-network) - always re-fetching on
        // every tab mount regardless of cache. Cached after the first load; the header's
        // Refresh button (MatterDetails.tsx) forces a re-fetch via MATTER_DETAILS_REFRESH_EVENT.
        options: ({ id }) => ({ variables: { id: id }, fetchPolicy: 'cache-first' })
    })
    (RvMatterFinancialsInfo)));
